/**
 * DegradedWidgetWrapper — applies muted styling to widgets that are
 * displaying stale cached data in the offline-degraded state.
 *
 * Wraps children with:
 *   - `aria-busy="true"` (communicates stale state to assistive tech)
 *   - `opacity-70 grayscale-[20%]` — visually signals data is not fresh
 *
 * Children should render normally with cached data. The wrapper communicates
 * "this is stale" without blocking interaction or hiding content.
 *
 * Only renders the wrapper when `isDegraded === true`; otherwise passes
 * children through unchanged.
 */
import type { ReactNode } from "react";

export interface DegradedWidgetWrapperProps {
  isDegraded: boolean;
  children: ReactNode;
  className?: string;
}

export function DegradedWidgetWrapper({
  isDegraded,
  children,
  className = "",
}: DegradedWidgetWrapperProps) {
  if (!isDegraded) return <>{children}</>;

  return (
    <div
      aria-busy="true"
      data-degraded="true"
      className={[
        "opacity-70",
        // CSS filter for grayscale — Tailwind 4 uses filter utilities
        "grayscale-[20%]",
        "transition-opacity duration-150",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {children}
    </div>
  );
}
